import { Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { SubStepEnum } from '../models/sub-step.enum';
import { Decoration } from '../../../../../models/configurators-2d/applications/fence/decoration';
import { MaterialsFacadeService } from '../services/materials.facade.service';
import { MaterialSubStepService } from '../services/material-sub-step.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Configuration } from '../../../../../models/configurators-2d/applications/fence/configuration';
import { TranslateService } from '@ngx-translate/core';
import { FenceService } from '../../../../../services/configurators-2d/applications/fence.service';
import { getMaxHeight } from '../../../../../services/helpers/fence-helper';
import { Side } from '../../../../../models/configurators-2d/applications/fence/side';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { DecorationStateService } from '../../services/decoration.state.service';
import { InstallationTypeEnum } from 'src/app/enum/fence/installation-type.enum';

@Component({
  selector: 'ng-casto-materials-decorative-boards',
  templateUrl: './materials-decorative-boards.component.html',
  styleUrl: './materials-decorative-boards.component.scss',
})
export class MaterialsDecorativeBoardsComponent implements OnInit {
  /** Base URL de l'API */
  baseImgURL: string = undefined;

  /** Langue sélectionnée */
  currentLang: string;

  protected readonly SubStepEnum = SubStepEnum;
  protected readonly InstallationTypeEnum = InstallationTypeEnum;
  protected readonly getMaxHeight = getMaxHeight;
  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly materialSubStepService = inject(MaterialSubStepService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly fenceService = inject(FenceService);
  private readonly decorationStateService = inject(DecorationStateService);

  constructor() {
    this.baseImgURL = this.fenceService.apiUrl;
  }

  /**
   * Retourne le message de déréférencement
   */
  protected get disableMessage(): string {
    return this.materialsFacadeService.disableMessage();
  }

  /**
   * Retourne la configuration actuelle
   */
  protected get configuration(): Configuration {
    return this.materialsFacadeService.configuration();
  }

  /**
   * Retourne la liste des lames décoratives disponibles
   */
  protected get decorativeBoards(): Decoration[] {
    return this.decorationStateService.decorativeBoards();
  }

  /**
   * Retourne la sous-étape courante du composant
   * @protected
   */
  protected get subStep(): SubStepEnum {
    return this.materialSubStepService.subStep();
  }

  /**
   * Vérifie si la liste de lames décoratives contient des éléments désactivés
   * @returns true si au moins une lame décorative est désactivée, false sinon
   */
  protected get isDecorativeBoardsValid(): boolean {
    return this.materialsFacadeService.isDecorativeBoardsValid();
  }

  /**
   * Est-ce que la liste des lames décoratives contient des éléments pouvant être placés horizontalement ?
   */
  hasHorizontalDecorativeBoards = computed(() => {
    return this.decorativeBoards.some(x => x.canBeHorizontal && !this.configuration.isVertical);
  });

  /**
   * Est-ce que la liste des lames décoratives contient des éléments pouvant être placés verticalement ?
   */
  hasVerticalDecorativeBoards = computed(() => {
    return this.decorativeBoards.some(x => x.canBeVertical && getMaxHeight(this.configuration) >= x.width);
  });

  /**
   * Est-ce que la liste des lames décoratives contient des éléments pouvant être placés horizontalement ou verticalement ?
   */
  hasDecorativeBoards = computed(() => {
    return this.hasHorizontalDecorativeBoards() || this.hasVerticalDecorativeBoards();
  });

  ngOnInit(): void {
    this.currentLang = this.translateService.getBrowserLang();
  }

  /**
   * Gère l'affichage/masquage des sous-menus en accordéon
   * @param step - Numéro du sous-menu (1: Lames, 2: Poteaux, 3: Lames décoratives, 4: Décors, 5: Panneaux, 6: Portillons)
   */
  protected toggleCollapse(step: SubStepEnum): void {
    this.materialSubStepService.toggleCollapse(step);
  }

  /**
   * Supprime toutes les lames décoratives de la configuration
   */
  protected deleteAllDecorativeBoards(): void {
    this.materialsFacadeService
      .deleteAllDecorativeBoards()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur sélection postType:', err),
      });
  }

  /**
   * Ajoute ou retire une lame décorative de la configuration
   * @param type - Type de lame décorative ('horizontal' ou 'vertical')
   * @param decorativeBoard - La lame décorative à ajouter/retirer
   */
  protected addDecorativeBoard(type: 'horizontal' | 'vertical', decorativeBoard: Decoration): void {
    this.materialsFacadeService
      .addDecorativeBoard(type, decorativeBoard)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur ajout lame décorative:', err),
      });
  }

  /**
   * Vérifie si une lame décorative est cochée dans la configuration
   * @param type - Type de lame décorative ('horizontal' ou 'vertical')
   * @param decorativeBoard - Lame décorative
   * @returns true si la lame décorative est cochée, false sinon
   */
  protected isDecorativeBoardChecked(type: 'horizontal' | 'vertical', decorativeBoard: Decoration): boolean {
    return this.materialsFacadeService.isDecorativeBoardChecked(type, decorativeBoard);
  }

  /**
   * Modifie la quantité d'une lame décorative
   * @param type - Type de lame décorative ('horizontal' ou 'vertical')
   * @param decorativeBoard - La lame décorative concernée
   * @param sideId - Identifiant du côté (pour les lames décoratives verticales), null pour les horizontales
   * @param event - L'événement de changement
   */
  protected changeDecorativeBoardQuantity(
    type: 'horizontal' | 'vertical',
    decorativeBoard: Decoration,
    sideId: string | null,
    event: Event
  ): void {
    this.materialsFacadeService
      .changeDecorativeBoardQuantity(type, decorativeBoard, sideId, event)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement quantité lame décorative:', err),
      });
  }

  /**
   * Calcule le nombre maximum d'éléments horizontaux (lames décoratives/décorations/panneaux) autorisés
   * Prend en compte le nombre de panneaux complets et les éléments déjà placés
   * @param element - Élément (lame décorative ou décoration ou panneau) pour lequel calculer le maximum
   * @returns Le nombre maximum d'éléments horizontaux autorisés
   */
  protected maxHorizontalElement(element: Decoration): number {
    return this.materialsFacadeService.maxHorizontalElement(element);
  }

  /**
   * Récupère la quantité d'une lame décorative dans la configuration
   * @param type - Type de lame décorative ('horizontal' ou 'vertical')
   * @param decorativeBoard - Lame décorative
   * @param side - Côté (pour les lames décoratives verticales), null pour les horizontales
   * @returns La quantité de la lame décorative
   */
  protected getDecorativeBoardQuantity(
    type: 'horizontal' | 'vertical',
    decorativeBoard: Decoration,
    side: Side | null
  ): number {
    return this.materialsFacadeService.getDecorativeBoardQuantity(type, decorativeBoard, side);
  }

  /**
   * Calcule le nombre maximum d'éléments verticaux (lames décoratives/décorations/panneaux) autorisés pour un côté
   * Prend en compte la longueur du côté et les éléments déjà placés
   * @param element - Élément (lame décorative ou décoration ou panneau) pour lequel calculer le maximum
   * @param side - Le côté de la clôture
   * @returns Le nombre maximum d'éléments verticaux autorisés pour ce côté
   */
  protected maxVerticalElement(element: Decoration, side: Side): number {
    return this.materialsFacadeService.maxVerticalElement(element, side);
  }
}
